import { useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  getGuidedTourStepsForPath,
  globalGuidedTourSteps,
  GuidedTourStep,
} from '../../config/guidedTourSteps';
import {
  cancelGuidedMode,
  finishGuidedMode,
  goToGuidedStep,
  guidedModeActiveSelector,
  guidedModeCurrentStepIndexSelector,
} from '../../state';

interface HighlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

type GuidedTourDescriptionOptions = Record<string, string | number>;

type ActiveGuidedTourStep = GuidedTourStep & {
  title?: string;
  description?: string;
  titleOptions?: GuidedTourDescriptionOptions;
  descriptionOptions?: GuidedTourDescriptionOptions;
};

const WIDGET_SELECTOR = '[data-tour="dashboard-widget-card"][data-dashboard-widget]';

const TourLayer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  inset: 0,
  zIndex: theme.zIndex.modal + 10,
  pointerEvents: 'none',
}));

const ScreenDimmer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  backgroundColor: alpha(theme.palette.common.black, 0.56),
}));

const HighlightBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  borderRadius: theme.shape.borderRadius * 1.5,
  border: `2px solid ${theme.palette.secondary.light}`,
  outline: `3px solid ${alpha(theme.palette.common.white, 0.92)}`,
  boxShadow: `0 0 0 9999px ${alpha(theme.palette.common.black, 0.56)}, 0 0 0 8px ${alpha(
    theme.palette.secondary.light,
    0.28
  )}`,
}));

const TourPaper = styled(Paper)(({ theme }) => ({
  maxWidth: 390,
  padding: theme.spacing(2),
  pointerEvents: 'auto',
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: `0 18px 44px ${alpha(theme.palette.common.black, 0.28)}`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
}));

const CenteredTourPaper = styled(TourPaper)(({ theme }) => ({
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: theme.zIndex.modal + 11,
}));

const getElementRect = (element: Element): HighlightRect => {
  const rect = element.getBoundingClientRect();
  const padding = 8;

  return {
    top: Math.max(rect.top - padding, 8),
    left: Math.max(rect.left - padding, 8),
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
  };
};

const isVisibleElement = (element: HTMLElement) => {
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

const areRectsEqual = (a: HighlightRect | null, b: HighlightRect | null) => {
  if (a === b) return true;
  if (!a || !b) return false;

  return a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;
};

const GuidedTourOverlay = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const isActive = useSelector(guidedModeActiveSelector);
  const currentStepIndex = useSelector(guidedModeCurrentStepIndexSelector);

  const panelRef = useRef<HTMLDivElement | null>(null);
  const widgetStepsCache = useRef<Map<string, ActiveGuidedTourStep>>(new Map());

  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [highlightRect, setHighlightRect] = useState<HighlightRect | null>(null);

  const routeSteps = useMemo(
    () => getGuidedTourStepsForPath(location.pathname),
    [location.pathname]
  );
  const isDashboardRoute = location.pathname === '/dashboard';

  const dynamicWidgetSteps = useMemo<ActiveGuidedTourStep[]>(() => {
    if (!isActive || !isDashboardRoute) return [];

    const elements = Array.from(document.querySelectorAll<HTMLElement>(WIDGET_SELECTOR)).filter(
      isVisibleElement
    );

    return elements.map((element, index) => {
      const widgetName = element.dataset.dashboardWidget || `widget-${index}`;
      const widgetLabel = element.dataset.tourWidgetLabel || widgetName.replaceAll('_', ' ');
      const id = `dashboard-widget-${widgetName}`;
      const widgetStepKey = `guidedMode.steps.dashboardWidget.items.${widgetName}`;
      const titleKey = i18n.exists(`${widgetStepKey}.title`)
        ? `${widgetStepKey}.title`
        : 'guidedMode.steps.dashboardWidget.title';
      const descriptionKey = i18n.exists(`${widgetStepKey}.description`)
        ? `${widgetStepKey}.description`
        : 'guidedMode.steps.dashboardWidget.description';

      const title = String(
        t(titleKey, {
          widget: widgetLabel,
          defaultValue: widgetLabel,
        })
      );
      const description = String(
        t(descriptionKey, {
          widget: widgetLabel,
          defaultValue: widgetLabel,
        })
      );
      const descriptionOptions = { widget: widgetLabel };
      const placement = 'top';
      const selector = `[data-dashboard-widget="${widgetName}"]`;

      const cachedStep = widgetStepsCache.current.get(id);

      if (cachedStep) {
        cachedStep.selector = selector;
        cachedStep.title = title;
        cachedStep.description = description;
        cachedStep.titleKey = titleKey;
        cachedStep.descriptionKey = descriptionKey;
        cachedStep.descriptionOptions = descriptionOptions;
        cachedStep.placement = placement;
        return cachedStep;
      }

      const newStep: ActiveGuidedTourStep = {
        id,
        selector,
        title,
        description,
        titleKey,
        descriptionKey,
        descriptionOptions,
        placement,
      };

      widgetStepsCache.current.set(id, newStep);
      return newStep;
    });
  }, [isActive, isDashboardRoute, i18n, t]);

  const tourSteps = useMemo<ActiveGuidedTourStep[]>(
    () => [...globalGuidedTourSteps, ...routeSteps, ...dynamicWidgetSteps],
    [dynamicWidgetSteps, routeSteps]
  );

  const totalSteps = tourSteps.length;
  const safeStepIndex =
    totalSteps > 0 ? Math.min(Math.max(currentStepIndex, 0), totalSteps - 1) : 0;
  const currentStep = totalSteps > 0 ? tourSteps[safeStepIndex] : null;
  const isFirstStep = safeStepIndex === 0;
  const isLastStep = safeStepIndex === totalSteps - 1;
  const titleId = currentStep ? `guided-tour-title-${currentStep.id}` : undefined;
  const descriptionId = currentStep ? `guided-tour-description-${currentStep.id}` : undefined;

  const progressValue = useMemo(
    () => (totalSteps > 0 ? ((safeStepIndex + 1) / totalSteps) * 100 : 0),
    [safeStepIndex, totalSteps]
  );

  const handleCancel = useCallback(() => {
    dispatch(cancelGuidedMode());
    enqueueSnackbar(t('guidedMode.notifications.cancelled'), { variant: 'info' });
  }, [dispatch, enqueueSnackbar, t]);

  const handleFinish = useCallback(() => {
    dispatch(finishGuidedMode());
    enqueueSnackbar(t('guidedMode.notifications.finished'), { variant: 'success' });
  }, [dispatch, enqueueSnackbar, t]);

  const handlePrevious = useCallback(() => {
    dispatch(goToGuidedStep(safeStepIndex - 1));
  }, [dispatch, safeStepIndex]);

  const handleNext = useCallback(() => {
    if (isLastStep) {
      handleFinish();
      return;
    }

    dispatch(goToGuidedStep(safeStepIndex + 1));
  }, [dispatch, handleFinish, isLastStep, safeStepIndex]);

  const refreshAnchorPosition = useCallback(() => {
    if (!currentStep) return;

    const targetElement = document.querySelector(currentStep.selector);
    const nextRect = targetElement ? getElementRect(targetElement) : null;

    setAnchorEl(prev => (prev === targetElement ? prev : targetElement));
    setHighlightRect(prev => (areRectsEqual(prev, nextRect) ? prev : nextRect));
  }, [currentStep]);

  useEffect(() => {
    if (!isActive || !currentStep) return undefined;

    // Avoid morphing animation from previous step rectangle (for example sidebar -> dashboard).
    setAnchorEl(null);
    setHighlightRect(null);

    const targetElement = document.querySelector(currentStep.selector);

    if (targetElement) {
      targetElement.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: 'auto',
      });
    }

    refreshAnchorPosition();

    const timeout = window.setTimeout(() => {
      refreshAnchorPosition();
    }, 220);

    return () => window.clearTimeout(timeout);
  }, [isActive, currentStep?.id, refreshAnchorPosition, currentStep]);

  useEffect(() => {
    if (!isActive) return undefined;

    let animationFrame = 0;

    const handleViewportChange = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(refreshAnchorPosition);
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [isActive, refreshAnchorPosition]);

  useEffect(() => {
    if (isActive) {
      panelRef.current?.focus();
    }
  }, [currentStep?.id, isActive]);

  useEffect(() => {
    if (!isActive) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleCancel();
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleNext();
      }

      if (event.key === 'ArrowLeft' && !isFirstStep) {
        event.preventDefault();
        handlePrevious();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleCancel, handleNext, handlePrevious, isActive, isFirstStep]);

  if (!isActive || !currentStep) return null;

  const stepTitle =
    currentStep.title ?? String(t(currentStep.titleKey, currentStep.titleOptions ?? {}));
  const stepDescription =
    currentStep.description ??
    String(t(currentStep.descriptionKey, currentStep.descriptionOptions ?? {}));

  const tourContent = (
    <TourPaper
      ref={panelRef}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      tabIndex={-1}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography variant="overline" color="primary.main">
              {t('guidedMode.progress', { current: safeStepIndex + 1, total: totalSteps })}
            </Typography>
            <Typography id={titleId} variant="h6">
              {stepTitle}
            </Typography>
          </Box>

          <IconButton
            aria-label={t('guidedMode.actions.cancel')}
            size="small"
            onClick={handleCancel}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Typography id={descriptionId} variant="body2" color="text.secondary">
          {anchorEl ? stepDescription : t('guidedMode.unavailable.description')}
        </Typography>

        <LinearProgress variant="determinate" value={progressValue} aria-hidden="true" />

        <Stack
          direction="row"
          spacing={1}
          justifyContent="space-between"
          flexWrap="wrap"
          useFlexGap
        >
          <Button onClick={handleCancel}>{t('guidedMode.actions.cancel')}</Button>

          <Stack direction="row" spacing={1}>
            <Button variant="outlined" disabled={isFirstStep} onClick={handlePrevious}>
              {t('guidedMode.actions.previous')}
            </Button>

            <Button variant="contained" onClick={handleNext} autoFocus>
              {isLastStep ? t('guidedMode.actions.finish') : t('guidedMode.actions.next')}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </TourPaper>
  );

  return (
    <TourLayer aria-live="polite">
      {!highlightRect && <ScreenDimmer />}

      {highlightRect && (
        <HighlightBox
          aria-hidden="true"
          sx={{
            top: highlightRect.top,
            left: highlightRect.left,
            width: highlightRect.width,
            height: highlightRect.height,
          }}
        />
      )}

      {anchorEl ? (
        <Popper
          open
          anchorEl={anchorEl}
          placement={currentStep.placement ?? 'bottom'}
          modifiers={[{ name: 'offset', options: { offset: [0, 16] } }]}
          sx={theme => ({ zIndex: theme.zIndex.modal + 11, pointerEvents: 'auto' })}
        >
          {tourContent}
        </Popper>
      ) : (
        <CenteredTourPaper
          ref={panelRef}
          role="dialog"
          aria-modal="false"
          aria-labelledby={titleId}
          aria-describedby={descriptionId}
          tabIndex={-1}
        >
          <Stack spacing={1.5}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="flex-start"
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="overline" color="primary.main">
                  {t('guidedMode.progress', { current: safeStepIndex + 1, total: totalSteps })}
                </Typography>
                <Typography id={titleId} variant="h6">
                  {t('guidedMode.unavailable.title')}
                </Typography>
              </Box>

              <IconButton
                aria-label={t('guidedMode.actions.cancel')}
                size="small"
                onClick={handleCancel}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Typography id={descriptionId} variant="body2" color="text.secondary">
              {t('guidedMode.unavailable.description')}
            </Typography>

            <LinearProgress variant="determinate" value={progressValue} aria-hidden="true" />

            <Stack
              direction="row"
              spacing={1}
              justifyContent="space-between"
              flexWrap="wrap"
              useFlexGap
            >
              <Button onClick={handleCancel}>{t('guidedMode.actions.cancel')}</Button>

              <Stack direction="row" spacing={1}>
                <Button variant="outlined" disabled={isFirstStep} onClick={handlePrevious}>
                  {t('guidedMode.actions.previous')}
                </Button>

                <Button variant="contained" onClick={handleNext} autoFocus>
                  {isLastStep ? t('guidedMode.actions.finish') : t('guidedMode.actions.next')}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </CenteredTourPaper>
      )}
    </TourLayer>
  );
};

export default GuidedTourOverlay;
